import type { CategoryMove, CategoryView, CreateProductRequest, ProductView } from "@kopi/contracts";
import {
  CATEGORY_SORTS,
  DEFAULT_CATEGORY_SORT,
  DEFAULT_PRODUCT_SORT,
  PRODUCT_SORTS,
  type SortValue,
} from "@kopi/contracts/client";
import { type ProductKind, SALES_CHANNELS, type SalesChannel } from "@kopi/contracts/codes";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import { Combobox } from "@kopi/ui/components/combobox";
import { ConfirmDialog } from "@kopi/ui/components/confirm-button";
import { DataTable, type DataTableColumn, type DataTableRowAction } from "@kopi/ui/components/data-table";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@kopi/ui/components/tabs";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  EyeIcon,
  EyeOffIcon,
  PencilIcon,
  PlusIcon,
  SquareArrowOutUpRightIcon,
} from "lucide-react";
import { type FormEvent, useId, useMemo, useState } from "react";
import {
  type CatalogueT,
  ChannelPicker,
  catalogueError,
  FormError,
  KIND_KEYS,
  kindOptions,
  NameEnField,
  ProductPhoto,
} from "@/components/catalogue";
import { AddModifierGroupDialog, ModifierGroups } from "@/components/modifier-groups";
import { Page, PageHeader } from "@/components/page-header";
import { IngredientDialog, Ingredients } from "@/components/recipes";
import { catalogue } from "@/i18n/messages/catalogue";
import { useMessages } from "@/i18n/provider";
import { api, unwrap } from "@/lib/api";
import { useConfirmTarget } from "@/lib/confirm-target";
import { nextDraftKey } from "@/lib/draft-key";
import { type StatusFilter, statusOptions, statusToActive } from "@/lib/filters";
import {
  categoriesKey,
  categoriesQuery,
  categoryLabel,
  fetchCategoryChoices,
  type ProductFilters,
  productsKey,
  productsQuery,
} from "@/lib/queries";
import { sortFrom, sortingOf, sortSearch, urlSorting } from "@/lib/sorting";

/** The column sort lives in the URL (R1-09d), so it survives a reload. */
const validateSearch = sortSearch(PRODUCT_SORTS);

export const Route = createFileRoute("/_authed/catalogue/")({
  staticData: { section: "workspace", crumb: "nav.catalogue" },
  validateSearch,
  loaderDeps: ({ search }) => ({ sort: search.sort }),
  loader: ({ context, deps }) =>
    Promise.all([
      context.queryClient.ensureInfiniteQueryData(categoriesQuery()),
      context.queryClient.ensureInfiniteQueryData(productsQuery({ q: "", sort: deps.sort })),
    ]),
  component: CataloguePage,
});

const allCategories = fetchCategoryChoices();
const activeCategories = fetchCategoryChoices(true);
const categoryValue = (c: CategoryView) => c.id;

type Tab = "products" | "categories" | "modifiers" | "ingredients";

const TABS: readonly Tab[] = ["products", "categories", "modifiers", "ingredients"];

function CataloguePage() {
  const { t } = useMessages(catalogue);
  const categories = useInfiniteQuery(categoriesQuery());
  const noCategories = categories.isSuccess && categories.data.pages[0]?.items.length === 0;
  const [tab, setTab] = useState<Tab>("products");
  const [adding, setAdding] = useState<Tab | null>(null);

  const action =
    tab === "products" ? (
      <Button type="button" onClick={() => setAdding("products")} disabled={noCategories}>
        <PlusIcon />
        {t("catalogue.addProduct")}
      </Button>
    ) : tab === "categories" ? (
      <Button type="button" onClick={() => setAdding("categories")}>
        <PlusIcon />
        {t("catalogue.addCategory")}
      </Button>
    ) : tab === "modifiers" ? (
      <Button type="button" onClick={() => setAdding("modifiers")}>
        <PlusIcon />
        {t("catalogue.modifiers.add")}
      </Button>
    ) : (
      <Button type="button" onClick={() => setAdding("ingredients")}>
        <PlusIcon />
        {t("catalogue.ingredients.add")}
      </Button>
    );

  return (
    <Page>
      <PageHeader title={t("nav.catalogue")} subtitle={t("catalogue.subtitle")} actions={action} />
      <Tabs value={tab} onValueChange={(value) => setTab(TABS.find((name) => name === value) ?? "products")}>
        <TabsList>
          <TabsTrigger value="products">{t("catalogue.tab.products")}</TabsTrigger>
          <TabsTrigger value="categories">{t("catalogue.tab.categories")}</TabsTrigger>
          <TabsTrigger value="modifiers">{t("catalogue.tab.modifiers")}</TabsTrigger>
          <TabsTrigger value="ingredients">{t("catalogue.tab.ingredients")}</TabsTrigger>
        </TabsList>
        <TabsContent value="products" className="mt-4">
          {noCategories && (
            <p className="mb-4 rounded-lg border border-dashed bg-card p-4 text-muted-foreground text-sm">
              {t("catalogue.categoryFirst")}
            </p>
          )}
          <Products />
        </TabsContent>
        <TabsContent value="categories" className="mt-4">
          <Categories />
        </TabsContent>
        <TabsContent value="modifiers" className="mt-4">
          <ModifierGroups />
        </TabsContent>
        <TabsContent value="ingredients" className="mt-4">
          <Ingredients />
        </TabsContent>
      </Tabs>
      {adding === "products" && <AddProductDialog onClose={() => setAdding(null)} />}
      {adding === "categories" && <CategoryDialog onClose={() => setAdding(null)} />}
      {adding === "modifiers" && <AddModifierGroupDialog onClose={() => setAdding(null)} />}
      {adding === "ingredients" && <IngredientDialog onClose={() => setAdding(null)} />}
    </Page>
  );
}

const productColumns = (t: CatalogueT): DataTableColumn<ProductView>[] => [
  {
    id: "name",
    header: t("catalogue.product"),
    enableSorting: true,
    meta: { card: "title" },
    cell: ({ row }) => (
      <Link
        to="/catalogue/$productId"
        params={{ productId: row.original.id }}
        className="flex min-w-0 items-center gap-3 rounded-md hover:underline"
      >
        <ProductPhoto url={row.original.thumbnailUrl} className="size-10" />
        <span className="min-w-0 truncate font-medium">{row.original.name}</span>
      </Link>
    ),
  },
  {
    id: "category",
    header: t("catalogue.category"),
    enableSorting: true,
    cell: ({ row }) => row.original.categoryName,
  },
  { id: "kind", header: t("catalogue.kind"), cell: ({ row }) => t(KIND_KEYS[row.original.kind]) },
  {
    id: "variants",
    header: t("catalogue.variants"),
    cell: ({ row }) => (
      <span className="text-muted-foreground">{row.original.variants.map((v) => v.name).join(", ")}</span>
    ),
  },
  {
    id: "status",
    header: t("common.status"),
    enableSorting: true,
    meta: { className: "md:w-28" },
    cell: ({ row }) =>
      row.original.active ? (
        <Badge variant="success">{t("common.active")}</Badge>
      ) : (
        <Badge variant="outline">{t("common.inactive")}</Badge>
      ),
  },
];

function Products() {
  const { t } = useMessages(catalogue);
  const columns = useMemo(() => productColumns(t), [t]);
  const navigate = useNavigate({ from: Route.fullPath });
  const { sort } = Route.useSearch();
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<{ id: string; name: string } | null>(null);
  const [status, setStatus] = useState<StatusFilter>("all");
  const filters: ProductFilters = { q, categoryId: category?.id, active: statusToActive(status), sort };
  const sorting = urlSorting(PRODUCT_SORTS, DEFAULT_PRODUCT_SORT, sort, (next) => {
    void navigate({ search: next ? { sort: next } : {}, replace: true });
  });
  const products = useInfiniteQuery(productsQuery(filters));

  return (
    <DataTable
      paging="pages"
      label={t("catalogue.products.list")}
      columns={columns}
      query={products}
      getRowId={(product) => product.id}
      estimateRowHeight={64}
      search={{ value: q, onChange: setQ, placeholder: t("catalogue.products.search") }}
      {...sorting}
      filters={
        <>
          <Combobox
            aria-label={t("catalogue.category")}
            className="md:w-52"
            fetchPage={allCategories}
            getValue={categoryValue}
            getLabel={categoryLabel}
            value={category?.id ?? null}
            selectedLabel={category?.name}
            onChange={(_, picked) => setCategory(picked && { id: picked.id, name: categoryLabel(picked) })}
            placeholder={t("catalogue.allCategories")}
            clearable
            clearLabel={t("catalogue.allCategories")}
            searchPlaceholder={t("catalogue.searchCategories")}
          />
          <OptionSelect
            aria-label={t("common.status")}
            className="md:w-40"
            options={statusOptions(t)}
            value={status}
            onValueChange={setStatus}
          />
        </>
      }
      rowActions={(product) => [
        {
          label: t("catalogue.openDetails"),
          icon: <SquareArrowOutUpRightIcon />,
          onSelect: () => void navigate({ to: "/catalogue/$productId", params: { productId: product.id } }),
        },
      ]}
      empty={{ title: t("catalogue.products.emptyTitle"), description: t("catalogue.products.emptyHint") }}
    />
  );
}

type CategoryDialogState = { kind: "rename"; category: CategoryView } | null;

function Categories() {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  // Kept on the screen, not in the URL: the catalogue's `?sort=` is the product list's.
  const [sort, setSort] = useState<SortValue<(typeof CATEGORY_SORTS)[number]>>();
  const categories = useInfiniteQuery(categoriesQuery(sort));
  /** Moving a category only reads right in menu order. */
  const menuOrder = sort === undefined;
  const items = categories.data?.pages.flatMap((p) => p.items) ?? [];
  const [dialog, setDialog] = useState<CategoryDialogState>(null);
  const deactivate = useConfirmTarget<CategoryView>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: categoriesKey });

  const update = useMutation({
    mutationFn: async ({ category, active }: { category: CategoryView; active: boolean }) =>
      unwrap(await api.v1.categories({ categoryId: category.id }).patch({ active })),
    onSuccess: async (_, { category, active }) => {
      await invalidate();
      toast.success(t(active ? "catalogue.reactivated" : "catalogue.deactivated", { name: category.name }));
    },
    onError: (error) => toast.error(catalogueError(t, error)),
  });
  // Swapped with its true neighbour on the server, in one transaction, even when that isn't loaded.
  const move = useMutation({
    mutationFn: async ({ category, direction }: { category: CategoryView; direction: CategoryMove }) =>
      unwrap(await api.v1.categories({ categoryId: category.id }).move.post({ direction })),
    onSuccess: invalidate,
    onError: (error) => toast.error(catalogueError(t, error)),
  });
  const busy = update.isPending || move.isPending;

  const columns = useMemo<DataTableColumn<CategoryView>[]>(
    () => [
      {
        id: "name",
        header: t("catalogue.category"),
        enableSorting: true,
        meta: { card: "title" },
        cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
      },
      {
        id: "status",
        header: t("common.status"),
        enableSorting: true,
        meta: { className: "md:w-32" },
        cell: ({ row }) =>
          row.original.active ? (
            <Badge variant="success">{t("common.active")}</Badge>
          ) : (
            <Badge variant="outline">{t("common.inactive")}</Badge>
          ),
      },
    ],
    [t],
  );

  const rowActions = (category: CategoryView): DataTableRowAction[] => {
    // The very first category can't go up; "down" on the last loaded one moves it past its true
    // neighbour on the server, loaded or not (a no-op at the end of the menu).
    const first = category.id === items[0]?.id;
    const last = category.id === items.at(-1)?.id && !categories.hasNextPage;
    return [
      {
        label: t("catalogue.rename"),
        icon: <PencilIcon />,
        onSelect: () => setDialog({ kind: "rename", category }),
      },
      {
        label: t("catalogue.moveUp"),
        icon: <ArrowUpIcon />,
        disabled: !menuOrder || first || busy,
        onSelect: () => move.mutate({ category, direction: "up" }),
      },
      {
        label: t("catalogue.moveDown"),
        icon: <ArrowDownIcon />,
        disabled: !menuOrder || last || busy,
        onSelect: () => move.mutate({ category, direction: "down" }),
      },
      category.active
        ? {
            label: t("common.deactivate"),
            icon: <EyeOffIcon />,
            destructive: true,
            disabled: busy,
            onSelect: () => deactivate.ask(category),
          }
        : {
            label: t("common.activate"),
            icon: <EyeIcon />,
            disabled: busy,
            onSelect: () => update.mutate({ category, active: true }),
          },
    ];
  };

  return (
    <>
      <DataTable
        paging="pages"
        label={t("catalogue.categories.list")}
        columns={columns}
        query={categories}
        getRowId={(category) => category.id}
        sorting={sortingOf(sort, DEFAULT_CATEGORY_SORT)}
        onSortingChange={(next) => setSort(sortFrom(CATEGORY_SORTS, next, DEFAULT_CATEGORY_SORT))}
        actions={
          !menuOrder && (
            <Button type="button" variant="outline" onClick={() => setSort(undefined)}>
              {t("catalogue.menuOrder")}
            </Button>
          )
        }
        rowActions={rowActions}
        empty={{
          title: t("catalogue.categories.emptyTitle"),
          description: t("catalogue.categories.emptyHint"),
        }}
      />
      {dialog?.kind === "rename" && (
        <CategoryDialog category={dialog.category} onClose={() => setDialog(null)} />
      )}
      <ConfirmDialog
        open={deactivate.open}
        onOpenChange={deactivate.onOpenChange}
        title={t("catalogue.categories.deactivateTitle", { name: deactivate.target?.name ?? "" })}
        description={t("catalogue.categories.deactivateHint")}
        confirmLabel={t("catalogue.categories.deactivate")}
        onConfirm={() => deactivate.target && update.mutate({ category: deactivate.target, active: false })}
      />
    </>
  );
}

/** Create a category (no `category`) or rename one. */
function CategoryDialog(props: { category?: CategoryView; onClose: () => void }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const [name, setName] = useState(props.category?.name ?? "");
  const [nameEn, setNameEn] = useState(props.category?.nameEn ?? "");
  const nameId = useId();
  const editing = props.category !== undefined;

  const save = useMutation({
    mutationFn: async () =>
      props.category
        ? unwrap(await api.v1.categories({ categoryId: props.category.id }).patch({ name, nameEn }))
        : // The server puts a new category last, whatever pages are loaded here.
          unwrap(await api.v1.categories.post({ name, nameEn })),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: categoriesKey });
      toast.success(editing ? t("catalogue.categories.saved") : t("catalogue.added", { name: name.trim() }));
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!save.isPending && name.trim()) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t(editing ? "catalogue.categories.edit" : "catalogue.addCategory")}
      description={t(editing ? "catalogue.categories.editHint" : "catalogue.categories.addHint")}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="category-form" disabled={save.isPending || !name.trim()}>
          {t(save.isPending ? "common.saving" : "common.save")}
        </Button>
      }
    >
      <form id="category-form" className="flex flex-col gap-3" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={nameId}>{t("catalogue.categories.name")}</Label>
          <Input id={nameId} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <NameEnField value={nameEn} onChange={setNameEn} />
        <FormError error={save.error} />
      </form>
    </ResponsiveDialog>
  );
}

interface VariantDraft {
  key: number;
  name: string;
  sku: string;
  barcode: string;
}

const newRow = (name = ""): VariantDraft => ({ key: nextDraftKey(), name, sku: "", barcode: "" });

function AddProductDialog(props: { onClose: () => void }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [kind, setKind] = useState<ProductKind>("beverage");
  const [channels, setChannels] = useState<SalesChannel[]>([...SALES_CHANNELS]);
  const [variants, setVariants] = useState<VariantDraft[]>(() => [newRow("Regular")]);
  const ids = { category: useId(), name: useId(), kind: useId() };

  const create = useMutation({
    mutationFn: async () => {
      if (!categoryId) throw new Error(t("catalogue.pickCategory"));
      const body: CreateProductRequest = {
        categoryId,
        name,
        nameEn,
        kind,
        channels,
        variants: variants.map((v) => ({ name: v.name, sku: v.sku || null, barcode: v.barcode || null })),
      };
      return unwrap(await api.v1.products.post(body));
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: productsKey });
      toast.success(t("catalogue.added", { name: name.trim() }));
      props.onClose();
    },
  });

  const setVariant = (key: number, change: Partial<VariantDraft>) =>
    setVariants((rows) => rows.map((r) => (r.key === key ? { ...r, ...change } : r)));
  const incomplete =
    !name.trim() || !categoryId || channels.length === 0 || variants.some((v) => !v.name.trim());

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!create.isPending && !incomplete) create.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("catalogue.addProduct")}
      description={t("catalogue.products.addHint")}
      dismissible={!create.isPending}
      className="md:max-w-2xl"
      footer={
        <Button type="submit" form="add-product" disabled={create.isPending || incomplete}>
          {t(create.isPending ? "common.saving" : "catalogue.products.save")}
        </Button>
      }
    >
      <form id="add-product" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor={ids.name}>{t("catalogue.products.name")}</Label>
            <Input id={ids.name} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <NameEnField value={nameEn} onChange={setNameEn} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.category}>{t("catalogue.category")}</Label>
            <Combobox
              id={ids.category}
              fetchPage={activeCategories}
              getValue={categoryValue}
              getLabel={categoryLabel}
              value={categoryId}
              onChange={setCategoryId}
              placeholder={t("catalogue.pickCategoryPlaceholder")}
              searchPlaceholder={t("catalogue.searchCategories")}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.kind}>{t("catalogue.kind")}</Label>
            <OptionSelect id={ids.kind} options={kindOptions(t)} value={kind} onValueChange={setKind} />
          </div>
        </div>
        <ChannelPicker value={channels} onChange={setChannels} />
        <fieldset className="flex flex-col gap-2">
          <legend className="mb-2 font-medium text-sm">{t("catalogue.variantsHint")}</legend>
          {variants.map((row) => (
            <div key={row.key} className="grid grid-cols-2 gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
              <Input
                aria-label={t("catalogue.variantName")}
                placeholder={t("catalogue.variantNamePlaceholder")}
                value={row.name}
                onChange={(e) => setVariant(row.key, { name: e.target.value })}
              />
              <Input
                aria-label={t("catalogue.sku")}
                placeholder={t("catalogue.skuPlaceholder")}
                value={row.sku}
                onChange={(e) => setVariant(row.key, { sku: e.target.value })}
              />
              <Input
                aria-label={t("catalogue.barcode")}
                placeholder={t("catalogue.barcodePlaceholder")}
                inputMode="numeric"
                value={row.barcode}
                onChange={(e) => setVariant(row.key, { barcode: e.target.value })}
              />
              {variants.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setVariants((rows) => rows.filter((r) => r.key !== row.key))}
                >
                  {t("catalogue.remove")}
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="self-start"
            onClick={() => setVariants((r) => [...r, newRow()])}
          >
            <PlusIcon />
            {t("catalogue.addVariant")}
          </Button>
        </fieldset>
        <FormError error={create.error} />
      </form>
    </ResponsiveDialog>
  );
}
